import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import MaintenanceGate from './components/common/MaintenanceGate';
import ScrollToTop from './components/common/ScrollToTop';

// Pages
import HomePage from './pages/home/HomePage';
import ShopPage from './pages/shop/ShopPage';
import ProductDetail from './pages/product/ProductDetail';
import CartPage from './pages/cart/CartPage';
import AboutPage from './pages/about/AboutPage';
import ContactPage from './pages/contact/ContactPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import OrderConfirmation from './pages/checkout/OrderConfirmation';
import SearchPage from './pages/search/SearchPage';

// Admin Components
import AdminLogin from './components/admin/AdminLogin';
import AdminPanel from './components/admin/AdminPanel';
import ForgotPassword from './components/admin/ForgotPassword';
import ResetPassword from './components/admin/ResetPassword';

// Import settings hook
import { useSettings } from './hooks/useSettings';

// Create a context for settings
export const SettingsContext = React.createContext();

// Default page background — used both as the fallback when the fetched
// setting is missing/empty, and as the color applied immediately so the
// page never flashes browser-default white before the fetch resolves.
const DEFAULT_BACKGROUND_COLOR = '#FAF8F4';

// Component to conditionally render Footer
const AppContent = () => {
  const { settings, loading, updateSettings, fetchSettings } = useSettings();
  const location = useLocation();

  const loadGlobalSettings = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        document.documentElement.style.setProperty('--primary-color', data.settings.primary_color || '#667eea');
        document.documentElement.style.setProperty('--secondary-color', data.settings.secondary_color || '#764ba2');

        // Guard against an empty/blank stored value overriding the real
        // default — a database row with '' would otherwise still apply
        // as background-color: ; and quietly do nothing visible, masking
        // the real intended default.
        const bgColor = data.settings.background_color?.trim() || DEFAULT_BACKGROUND_COLOR;
        document.documentElement.style.setProperty('--background-color', bgColor);
        document.body.style.backgroundColor = bgColor;
      }
    } catch (error) {
      console.error('Error loading global settings:', error);
    }
  };

  useEffect(() => {
    // Apply the default immediately on mount, before the fetch even starts,
    // so the page never flashes browser-default white (or a stale color)
    // while loadGlobalSettings() is still in flight.
    document.body.style.backgroundColor = DEFAULT_BACKGROUND_COLOR;

    loadGlobalSettings();

    const handleSettingsUpdate = () => {
      loadGlobalSettings();
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const isAdminRoute = location.pathname.startsWith('/admin');

  if (loading) {
    return <div className="loader-container"><div className="loader">Loading</div></div>;
  }

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, fetchSettings }}>
      <div className="App">
        {!isAdminRoute && <Navbar />}
        <main className="main-content">
          {/* MaintenanceGate now wraps the REAL Routes block — no nested
              Router, no placeholder comment. */}
          <MaintenanceGate>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              <Route path="/search" element={<SearchPage />} />

              {/* Admin Login - Direct access - NO AUTH REQUIRED */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Admin Panel - All admin routes go through AdminPanel.
                  Storefront and StoreSettings live INSIDE AdminPanel.jsx's
                  own nested <Routes>, not here — see note below. */}
              <Route path="/admin/*" element={<AdminPanel />} />

              {/* Redirect /admin to /admin/dashboard */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

              {/* 404 Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </MaintenanceGate>
        </main>
        {!isAdminRoute && <Footer />}
      </div>
    </SettingsContext.Provider>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;